/**
 * A build's recommended gear in the in-game character window. Each slot opens the item's game
 * tooltip on hover and focus, with the build's notes (enchant, upgrade, owner's choice, source) under it; the full table with
 * sources stays one disclosure below it on the build page. The build's owner also gets a menu
 * beside each slot the agent gave other items for.
 */
import {
  BUILD_SLOT_LABELS,
  CHARACTER_SLOT_LABELS,
  CHARACTER_SLOT_TYPES,
  buildSlotToCharacterSlot,
  type BuildGearRow,
  type BuildSheetView,
  type BuildSlot,
  type BuildView,
} from '@thottbot/shared';
import { PaperDoll, type PaperDollSlot } from '../characters/PaperDoll';
import { canChoose, gearChoices, selectionFor } from './gearChoices';

/** The item's own lines: what a fetched tooltip shows, kept for screen readers. */
function itemLines(row: BuildGearRow): string[] {
  const out: string[] = [];
  const item = row.item;
  if (item.itemLevel !== undefined) out.push(`Item level ${item.itemLevel}`);
  if (item.armor) out.push(`${item.armor} Armor`);
  if (item.damage) out.push(`${item.damage.min} - ${item.damage.max} damage, speed ${item.damage.speed.toFixed(2)}`);
  for (const s of item.stats ?? []) out.push(`${s.value > 0 ? '+' : ''}${s.value} ${s.name}`);
  return out;
}

/** What the build says about this slot, under the item's tooltip. */
function buildNotes(row: BuildGearRow): string[] {
  const out: string[] = [];
  if (row.enchant?.text) out.push(`Enchant: ${row.enchant.text}`);
  if (row.upgrade) out.push('Upgrade');
  if (!row.options.find((o) => o.item.artifactId === row.selected)?.recommended) out.push("Chosen by the build's owner");
  const sources = row.item.sources ?? [];
  out.push(
    row.item.sourceUnknown || sources.length === 0
      ? 'Source unknown'
      : `Source: ${sources
          .slice(0, 2)
          .map((s) => (s.kind === 'dungeon' || s.kind === 'raid' ? `${s.name} (${s.kind})` : s.name))
          .join(', ')}`,
  );
  return out;
}

/** The owner's menu of the agent's other items for a slot, beside its icon. */
function SlotMenu({ row, gear, onSelect }: { row: BuildGearRow; gear: BuildGearRow[]; onSelect: (slot: BuildSlot, artifactId: string | null) => void }) {
  return (
    <span className="sheet-slot-menu">
      <select aria-label={`${BUILD_SLOT_LABELS[row.slot]} options`} value={row.selected} onChange={(e) => onSelect(row.slot, selectionFor(row, e.target.value))}>
        {gearChoices(row, gear).map((c) => (
          <option key={c.value} value={c.value} disabled={c.disabled}>
            {c.label}
          </option>
        ))}
      </select>
    </span>
  );
}

export interface BuildSheetProps {
  character: BuildView['character'];
  gear: BuildGearRow[];
  sheet: BuildSheetView;
  /** The owner of a ready build may change each slot from the sheet. */
  editable?: boolean;
  /** `null` goes back to the agent's pick. */
  onSelect?: (slot: BuildSlot, artifactId: string | null) => void;
  /** The last save that failed. */
  error?: { slot: BuildSlot; message: string } | null;
}

export function BuildSheet({ character, gear, sheet, editable = false, onSelect, error }: BuildSheetProps) {
  // An off hand under a two-handed weapon is not worn, so its slot shows empty.
  const bySlot = new Map(gear.filter((g) => !g.blockedBy).map((g) => [buildSlotToCharacterSlot(g.slot), g]));
  const slots: PaperDollSlot[] = CHARACTER_SLOT_TYPES.map((slot) => {
    const row = bySlot.get(slot);
    return {
      slot,
      label: CHARACTER_SLOT_LABELS[slot],
      item: row ? { name: row.item.name, slug: row.item.slug, iconUrl: row.item.iconUrl, quality: row.item.quality } : null,
      lines: row ? itemLines(row) : [],
      contextLines: row ? buildNotes(row) : [],
      artUrl: sheet.slotArtUrls[slot],
      menu: editable && onSelect && row && canChoose(row) ? <SlotMenu row={row} gear={gear} onSelect={onSelect} /> : undefined,
    };
  });
  const summary = `Level ${character.level} ${character.race} ${character.class}`;
  return (
    <>
      <PaperDoll
        testId="build-sheet"
        title={character.characterName ?? 'Recommended gear'}
        classIconUrl={sheet.classIconUrl}
        classLabel={character.class}
        plate={
          <>
            <div className="sheet-level">{summary}</div>
            <div className="muted">
              {character.role} · {character.faction}
            </div>
          </>
        }
        slots={slots}
        attributes={sheet.attributes}
        attributesNote={sheet.attributes.itemsEquipped === 0 ? 'No gear to sum.' : 'Recommended gear only; base stats not included.'}
      />
      {error && (
        <p className="error" role="alert">
          {BUILD_SLOT_LABELS[error.slot]}: {error.message}
        </p>
      )}
    </>
  );
}
